import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { LogIn, Sparkles, Eye, EyeOff, Mail, Lock, User as UserIcon } from "lucide-react";
import { LOGO_URL } from "../lib/api";
import { toast } from "sonner";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login, emailLogin, emailRegister } = useAuth();
  const { t } = useSettings();
  const nav = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { document.title = "Sign In – Cheap4Teach"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please enter your email and password"); return; }
    if (mode === "signup" && password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      if (mode === "signin") {
        await emailLogin(email, password);
      } else {
        await emailRegister(email, password, name);
      }
      const next = localStorage.getItem("c4t_after_login") || "/";
      localStorage.removeItem("c4t_after_login");
      nav(next, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="login-page">
      <div className="c4t-card">
        <img src={LOGO_URL} alt="Cheap4Teach" className="mx-auto h-20 logo-blend" />
        <h1 className="text-3xl font-['Fredoka'] font-bold mt-4 text-center">
          {mode === "signin" ? t("login.title", "Welcome back, Teacher!") : "Create your account"}
        </h1>
        <p className="c4t-desc mt-2 flex items-center justify-center gap-2 text-center">
          <Sparkles className="w-4 h-4 text-[#FFC500]" />
          {mode === "signin" ? "Sign in to place orders & track your history" : "Join us! It only takes a moment"}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-3">
          {mode === "signup" && (
            <div>
              <label className="text-sm font-semibold flex items-center gap-1.5"><UserIcon className="w-4 h-4" /> Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                className="c4t-input mt-1" placeholder="Your full name" data-testid="login-name-input" />
            </div>
          )}
          <div>
            <label className="text-sm font-semibold flex items-center gap-1.5"><Mail className="w-4 h-4" /> Email</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
              className="c4t-input mt-1" placeholder="teacher@school.com" data-testid="login-email-input" />
          </div>
          <div>
            <label className="text-sm font-semibold flex items-center gap-1.5"><Lock className="w-4 h-4" /> Password</label>
            <div className="relative mt-1">
              <input type={showPwd ? "text" : "password"} required value={password} onChange={e => setPassword(e.target.value)}
                className="c4t-input pr-12" placeholder={mode === "signup" ? "At least 6 characters" : "Your password"}
                data-testid="login-password-input" />
              <button type="button" onClick={() => setShowPwd(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#455A64] hover:text-[#FF1F87]"
                aria-label={showPwd ? "Hide password" : "Show password"}
                data-testid="login-password-toggle">
                {showPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={busy} className="c4t-btn c4t-btn-pink w-full !mt-5" data-testid="login-submit-btn">
            <LogIn className="w-5 h-5" />
            {busy ? "Please wait..." : (mode === "signin" ? "Sign In" : "Create Account")}
          </button>
          {mode === "signin" && (
            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-[#118AB2] hover:text-[#FF477E] font-semibold" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          )}
        </form>

        <p className="text-center text-sm c4t-desc mt-4">
          {mode === "signin" ? (
            <>Don't have an account?{" "}
              <button type="button" onClick={() => setMode("signup")} className="text-[#FF1F87] font-bold hover:underline" data-testid="login-switch-signup">
                Create one
              </button>
            </>
          ) : (
            <>Already have an account?{" "}
              <button type="button" onClick={() => setMode("signin")} className="text-[#FF1F87] font-bold hover:underline" data-testid="login-switch-signin">
                Sign in
              </button>
            </>
          )}
        </p>

        <div className="my-5 flex items-center gap-3">
          <div className="flex-1 h-px bg-black/15" />
          <span className="text-xs font-bold text-[#455A64] tracking-wider">OR</span>
          <div className="flex-1 h-px bg-black/15" />
        </div>

        <button onClick={login} className="c4t-btn c4t-btn-white w-full" data-testid="google-login-btn">
          <svg className="w-5 h-5" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
            <path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5h-1.9V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C40.5 36 44 30.5 44 24c0-1.2-.1-2.4-.4-3.5z"/>
          </svg>
          Continue with Google
        </button>

        <p className="text-xs c4t-desc mt-4 text-center">
          By signing in you agree to our friendly terms. Need help? <Link to="/contact" className="text-[#118AB2] underline">Contact us</Link>.
        </p>
      </div>
    </div>
  );
}
